import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Generic per-page SEO component.
 *
 * Uses the Helmet props API (title, meta, link, script) rather than children
 * to avoid "Helmet expects a string as a child of <title>" runtime errors
 * when title becomes an empty string at edge cases.
 */
export const PageSEO = ({
  title,
  description,
  canonical,
  image,
  structuredData,
  noIndex = false,
}) => {
  const fullTitle = (title && String(title).trim())
    ? `${title} — DoctoDays`
    : "DoctoDays — Prenez rendez-vous avec votre médecin en ligne";

  const desc =
    (description && String(description).trim()) ||
    "DoctoDays : la plateforme française pour trouver un médecin et prendre rendez-vous en ligne 24h/24.";

  const ogImage = image || "https://doctodays.com/brand/doctodays-logo-1024.png";

  const metaTags = [
    { name: "description", content: desc },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: desc },
    { property: "og:image", content: ogImage },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: desc },
    { name: "twitter:image", content: ogImage },
  ];
  if (canonical) metaTags.push({ property: "og:url", content: canonical });
  if (noIndex) metaTags.push({ name: "robots", content: "noindex, follow" });

  const linkTags = canonical ? [{ rel: "canonical", href: canonical }] : [];

  const scriptTags = structuredData
    ? [
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify(structuredData),
        },
      ]
    : [];

  return (
    <Helmet
      title={fullTitle}
      meta={metaTags}
      link={linkTags}
      script={scriptTags}
    />
  );
};

/**
 * Build a schema.org/Physician object for a doctor detail page.
 * Includes embedded Review[] for rich snippets (stars in Google search).
 */
export const buildPhysicianSchema = (doctor, reviews = []) => {
  if (!doctor) return null;
  const fullName = `Dr. ${doctor.first_name || ""} ${doctor.last_name || ""}`.trim();
  const schema = {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: fullName,
    url: `https://doctodays.com/doctor/${doctor.id}`,
    image: doctor.photo_url || "https://doctodays.com/brand/doctodays-logo-1024.png",
    medicalSpecialty: doctor.specialty || undefined,
    address:
      doctor.address || doctor.city
        ? {
            "@type": "PostalAddress",
            streetAddress: doctor.address || undefined,
            addressLocality: doctor.city || undefined,
            addressCountry: "FR",
          }
        : undefined,
    telephone: doctor.phone || undefined,
    priceRange:
      doctor.consultation_fee != null ? `${doctor.consultation_fee} €` : undefined,
    aggregateRating:
      doctor.rating && doctor.rating > 0
        ? {
            "@type": "AggregateRating",
            ratingValue: Number(doctor.rating).toFixed(1),
            reviewCount: doctor.review_count || (reviews ? reviews.length : 1),
            bestRating: 5,
            worstRating: 1,
          }
        : undefined,
  };

  // Embed individual reviews (Google needs at least 1 review next to aggregateRating
  // to display stars on SERP). Limit to 5 most recent for payload size.
  if (Array.isArray(reviews) && reviews.length > 0) {
    schema.review = reviews.slice(0, 5).map((r) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: r.patient_name || r.author || "Patient anonyme",
      },
      datePublished: r.created_at
        ? new Date(r.created_at).toISOString().split("T")[0]
        : undefined,
      reviewRating: {
        "@type": "Rating",
        ratingValue: Number(r.rating || 5),
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody: r.comment || r.review || undefined,
      itemReviewed: {
        "@type": "Physician",
        name: fullName,
      },
    }));
  }

  return schema;
};

export default PageSEO;
